import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import Collapse from '@mui/material/Collapse';
import classNames from 'classnames';
import css from './FiltersSection.css';
import ExpandButton from '../ExpandButton';
import AutoCompleteField from '../AutoCompleteField';
import FiltersPopover from './FiltersPopover';
import FiltersSectionHeader from './FiltersSectionHeader';
import { IconSizes } from '../../foundations/Icon';

const FiltersSection = ({
    filters,
    filtersByCategory = {},
    onFilterTagsChange,
    disabled = false,
    onClearAll = null,
    headerTitle = '',
    containerClass = '',
    contentClass = '',
    onFilterAdded = null,
    onFilterRemoved = null,
    addFilterText = 'STATIC.FILTERS_SECTION.ADD_FILTER',
    addFilterIconName,
    headerIcon = null,
    PopoverIconComponent = null,
    openFiltersByDefault = false,
    shouldWarnEmpty = false,
}) => {
    const [expanded, setExpanded] = useState(true);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [latestAddedFilterName, setLatestAddedFilterName] = useState(null);
    const addFilterButtonRef = useRef(null);

    const appliedFiltersCount = Object.keys(filters).reduce(
        (count, name) => count + (filters[name]?.selectedOptions?.length ? 1 : 0),
        0
    );

    const selectedFilterNames = useMemo(() => Object.keys(filters), [filters]);

    const allFiltersCount = useMemo(
        () =>
            Object.keys(filtersByCategory).reduce(
                (count, category) => count + (filtersByCategory[category]?.length || 0),
                0
            ),
        [filtersByCategory]
    );

    const handleFilterAdded = useCallback(
        name => {
            if (selectedFilterNames.includes(name)) return;

            onFilterAdded(name);
            setIsPopoverOpen(false);
            openFiltersByDefault && setLatestAddedFilterName(name);
        },
        [onFilterAdded, selectedFilterNames, openFiltersByDefault]
    );

    const isMaxFiltersSelected = selectedFilterNames.length === allFiltersCount;

    return (
        <div className={classNames(css.collapsableSection, containerClass)}>
            <Collapse orientation="vertical" collapsedSize={50} in={expanded}>
                <div className={css.filterSection}>
                    <FiltersSectionHeader
                        expanded={expanded}
                        setExpanded={setExpanded}
                        onClearClicked={onClearAll}
                        appliedFiltersCount={appliedFiltersCount}
                        disabled={disabled}
                        title={headerTitle}
                        icon={headerIcon}
                    />
                    <div className={classNames(css.content, contentClass)}>
                        {selectedFilterNames.map(filterName => {
                            const filter = filters[filterName];
                            return (
                                <AutoCompleteField
                                    key={filterName}
                                    value={filter.selectedOptions || []}
                                    advanced
                                    label={filter.displayName || filterName}
                                    options={filter.options || []}
                                    onChange={tags => onFilterTagsChange(filterName, tags)}
                                    disabled={disabled}
                                    loading={filter.loading}
                                    multiple
                                    removable={filter.removable !== false}
                                    onRemove={() => onFilterRemoved(filterName)}
                                    containerClass={css.filterDropdown}
                                    defaultOpen={filterName === latestAddedFilterName}
                                    shouldWarnEmpty={shouldWarnEmpty}
                                />
                            );
                        })}
                        <ExpandButton
                            ref={addFilterButtonRef}
                            text={<Translate id={addFilterText} />}
                            icon={addFilterIconName || 'add-fill'}
                            iconSize={IconSizes.MEDIUM}
                            onClick={() => setIsPopoverOpen(true)}
                            isExpanded={isPopoverOpen}
                            containerClass={classNames(css.filterDropdown, css.addFilterButton)}
                            disabled={disabled || isMaxFiltersSelected}
                        />
                    </div>
                </div>
            </Collapse>
            <FiltersPopover
                open={isPopoverOpen}
                position={addFilterButtonRef.current}
                onClose={() => setIsPopoverOpen(false)}
                filtersByCategory={filtersByCategory}
                selectedFilters={selectedFilterNames}
                onFilterSelect={handleFilterAdded}
                IconComponent={PopoverIconComponent}
            />
        </div>
    );
};

FiltersSection.propTypes = {
    filters: PropTypes.objectOf(
        PropTypes.shape({
            name: PropTypes.string,
            displayName: PropTypes.string,
            options: PropTypes.arrayOf(
                PropTypes.shape({
                    label: PropTypes.string,
                    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                })
            ),
            selectedOptions: PropTypes.arrayOf(
                PropTypes.shape({
                    label: PropTypes.string,
                    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                })
            ),
            loading: PropTypes.bool,
            removable: PropTypes.bool,
        })
    ).isRequired,
    filtersByCategory: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    onFilterTagsChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    onClearAll: PropTypes.func,
    headerTitle: PropTypes.string,
    containerClass: PropTypes.string,
    contentClass: PropTypes.string,
    onFilterAdded: PropTypes.func,
    onFilterRemoved: PropTypes.func,
    addFilterText: PropTypes.string,
    addFilterIconName: PropTypes.string,
    headerIcon: PropTypes.string,
    PopoverIconComponent: PropTypes.func,
    openFiltersByDefault: PropTypes.bool,
    shouldWarnEmpty: PropTypes.bool,
};

export default FiltersSection;
